import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listRichProducts, publishProduct, draftProduct, type RichProduct } from "@/lib/products";
import { format } from "date-fns";
import {
  Plus,
  Zap,
  FileText,
  Clock,
  Eye,
  EyeOff,
  Layers,
  Search,
  Grid3X3,
  LayoutGrid,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute("/app/staff/products/")({
  component: StaffProductsList,
});

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-muted text-muted-foreground", Icon: FileText },
  scheduled: { label: "Scheduled", cls: "bg-amber-100 text-amber-700", Icon: Clock },
  live: { label: "Live", cls: "bg-emerald-100 text-emerald-700", Icon: Zap },
};

function StaffProductsList() {
  const qc = useQueryClient();
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["rich-products"],
    queryFn: listRichProducts,
  });
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"table" | "grid">("table");

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.product_type || "").toLowerCase().includes(q) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(q)),
    );
  }, [products, search]);

  const handleToggle = async (p: RichProduct) => {
    try {
      if (p.status === "live") {
        await draftProduct(p.id);
        toast.success(`"${p.name}" set to draft`);
      } else {
        await publishProduct(p.id);
        toast.success(`"${p.name}" published`);
      }
      qc.invalidateQueries({ queryKey: ["rich-products"] });
    } catch {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Products</h1>
          <p className="text-muted-foreground">Manage your product catalogue.</p>
        </div>
        <Link to="/app/staff/products/$productId" params={{ productId: "new" }}>
          <Button className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90 gap-1.5">
            <Plus className="h-4 w-4" /> New product
          </Button>
        </Link>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9 rounded-full"
            placeholder="Search by name, type, or tag"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1 rounded-xl border border-border p-1">
          <button
            onClick={() => setView("table")}
            className={cn(
              "p-1.5 rounded-lg transition-colors",
              view === "table"
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted text-muted-foreground",
            )}
            title="Table view"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setView("grid")}
            className={cn(
              "p-1.5 rounded-lg transition-colors",
              view === "grid"
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted text-muted-foreground",
            )}
            title="Grid view"
          >
            <Grid3X3 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-2xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl border border-border bg-card p-12 text-center shadow-soft">
          <Layers className="mx-auto mb-4 h-10 w-10 text-muted-foreground/40" />
          <p className="text-muted-foreground">
            {search ? "No products match your search." : "No products yet."}
          </p>
          {!search && (
            <Link
              to="/app/staff/products/$productId"
              params={{ productId: "new" }}
              className="mt-4 inline-block"
            >
              <Button className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90 gap-1.5">
                <Plus className="h-4 w-4" /> Create first product
              </Button>
            </Link>
          )}
        </div>
      ) : view === "table" ? (
        <div className="rounded-3xl border border-border bg-card shadow-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left py-3 px-4 font-medium">Name</th>
                <th className="text-left py-3 px-4 font-medium hidden sm:table-cell">Type</th>
                <th className="text-left py-3 px-4 font-medium hidden md:table-cell">Price/m</th>
                <th className="text-left py-3 px-4 font-medium">Status</th>
                <th className="text-left py-3 px-4 font-medium hidden lg:table-cell">
                  Gang sheet?
                </th>
                <th className="text-left py-3 px-4 font-medium hidden lg:table-cell">Updated</th>
                <th className="py-3 px-4" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const meta = STATUS_META[p.status] ?? STATUS_META.draft;
                const Icon = meta.Icon;
                return (
                  <tr
                    key={p.id}
                    className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {p.image_main_url ? (
                          <img
                            src={p.image_main_url}
                            alt=""
                            className="h-9 w-9 rounded-xl object-cover shrink-0"
                          />
                        ) : (
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted">
                            <Layers className="h-4 w-4 text-muted-foreground/40" />
                          </div>
                        )}
                        <div>
                          <Link
                            to="/app/staff/products/$productId"
                            params={{ productId: p.id }}
                            className="font-medium hover:text-primary transition-colors"
                          >
                            {p.name || (
                              <span className="text-muted-foreground italic">Untitled</span>
                            )}
                          </Link>
                          {p.short_description && (
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {p.short_description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground hidden sm:table-cell">
                      {p.product_type || "—"}
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      £{parseFloat(String(p.price_per_m) || "0").toFixed(2)}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                          meta.cls,
                        )}
                      >
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="py-3 px-4 hidden lg:table-cell">
                      {p.is_gangsheet ? (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          Yes
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground hidden lg:table-cell">
                      {p.updated_at ? format(new Date(p.updated_at), "d MMM yyyy") : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => handleToggle(p)}
                          title={p.status === "live" ? "Set to draft" : "Publish"}
                          className="rounded-xl p-1.5 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                        >
                          {p.status === "live" ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                        <Link
                          to="/app/staff/products/$productId"
                          params={{ productId: p.id }}
                          className="rounded-xl p-1.5 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                        >
                          <FileText className="h-4 w-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => {
            const meta = STATUS_META[p.status] ?? STATUS_META.draft;
            const Icon = meta.Icon;
            return (
              <Link
                key={p.id}
                to="/app/staff/products/$productId"
                params={{ productId: p.id }}
                className="group relative overflow-hidden rounded-3xl border border-border bg-card shadow-soft transition-all hover:-translate-y-1 hover:shadow-pop"
              >
                {p.image_main_url ? (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image_main_url}
                      alt=""
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-muted flex items-center justify-center">
                    <Layers className="h-10 w-10 text-muted-foreground/30" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold leading-tight">{p.name || "Untitled"}</h3>
                    <span
                      className={cn(
                        "shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                        meta.cls,
                      )}
                    >
                      <Icon className="h-3 w-3" /> {meta.label}
                    </span>
                  </div>
                  {p.short_description && (
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {p.short_description}
                    </p>
                  )}
                  <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                    <span>£{parseFloat(String(p.price_per_m) || "0").toFixed(2)}/m</span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleToggle(p);
                      }}
                      className="rounded-lg p-1 hover:bg-muted transition-colors"
                      title={p.status === "live" ? "Set to draft" : "Publish"}
                    >
                      {p.status === "live" ? (
                        <EyeOff className="h-3.5 w-3.5" />
                      ) : (
                        <Eye className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
